import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Components
import { SelectOptionProps } from "components/atoms/Select";

// Types
import {
  PAYMENT_STEPS,
  PaymentMethodType,
  PAYMENT_TYPES,
  SetMethodType,
  BankDataResponse,
  PaymentAppsType,
  BanksApiResponse,
  BanksResponsePaymentMethodCode,
} from "types/PaymentMethodsTypes";
import { ChangeNumberValues, ChangeStringValues } from "types/Auth";

// Utils
import { mapBanksB2F } from "mappings/banks";
import { PAYMENT_METHODS_CODES } from "constants/paymentMethodsConst";

export type PaymentState = {
  account: string;
  accountNumber: string;
  amount: string;
  birthdate: string;
  email: string;
  depositMethods: PaymentMethodType[] | null;
  withdrawMethods: PaymentMethodType[] | null;
  cryptoMethods: BanksApiResponse | null;
  method: PaymentMethodType | null;
  cryptoMethod?: BanksResponsePaymentMethodCode | null;
  qrCode: string;
  bank: number | null;
  banks: SelectOptionProps[] | null;
  bankCode: string | null;
  branchCode: string;
  expire: string;
  idNumber: string;
  idDate: string;
  iban: string;
  ifscCode: string;
  name: string;
  surname: string;
  paymentApp: PaymentAppsType | null;
  paymentPage: string;
  qrCodeData: string;
  lifeTime: number | null;
  card: string;
  wallet: string;
  phone: string;
  status: keyof typeof PAYMENT_STEPS;
  transaction: string;
  transactionId: string;
  type: PAYMENT_TYPES | null;
  error: string | null;
  isLoading: boolean;
  bkashType: string;
  expDate: string;
  minAmount: number | null;
  walletAdress: string;
  serviceError: boolean | null;
  destinationTag: string;
};

const initialState: PaymentState = {
  account: "",
  accountNumber: "",
  amount: "",
  birthdate: "",
  bkashType: "",
  expire: "",
  card: "",
  email: "",
  phone: "",
  transaction: "",
  transactionId: "",
  bank: null,
  banks: null,
  bankCode: null,
  branchCode: "",
  idNumber: "",
  idDate: "",
  iban: "",
  ifscCode: "",
  name: "",
  surname: "",
  paymentApp: null,
  paymentPage: "",
  qrCodeData: "",
  lifeTime: null,
  wallet: "",
  depositMethods: null,
  withdrawMethods: null,
  cryptoMethods: null,
  method: null,
  cryptoMethod: null,
  qrCode: "",
  status: PAYMENT_STEPS.DEPOSIT_AMOUNT,
  type: null,
  error: null,
  isLoading: false,
  expDate: "",
  minAmount: null,
  serviceError: null,
  walletAdress: "",
  destinationTag: "",
};

export const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    setAccount(state, action: PayloadAction<string>) {
      state.account = action.payload;
      state.error = null;
    },
    setBkashAccount(state, action: PayloadAction<string>) {
      state.bkashType = action.payload;
    },
    // eslint-disable-next-line consistent-return
    stepBack(state, action: PayloadAction<PAYMENT_TYPES>) {
      if (state.method) {
        const indexOfStep = state.method.steps[action.payload].indexOf(
          state.status,
        );
        if (indexOfStep <= 0) {
          return {
            ...state,
            account: "",
            accountNumber: "",
            amount: "",
            bankCode: null,
            birthdate: "",
            bkashType: "",
            expire: "",
            card: "",
            email: "",
            phone: "",
            transaction: "",
            bank: null,
            branchCode: "",
            idNumber: "",
            idDate: "",
            iban: "",
            ifscCode: "",
            name: "",
            surname: "",
            paymentPage: "",
            paymentApp: null,
            qrCodeData: "",
            lifeTime: null,
            wallet: "",
            method: null,
            cryptoMethod: null,
            qrCode: "",
            status: PAYMENT_STEPS.DEPOSIT_AMOUNT,
            type: null,
            error: null,
            expData: "",
            minAmount: null,
            isLoading: false,
            walletAdress: "",
            destinationTag: "",
          };
        }
        if (indexOfStep > 0) {
          state.status = state.method.steps[action.payload][indexOfStep - 1];
        }
      }
      state.error = null;
    },
    stepNext(state, action: PayloadAction<PAYMENT_TYPES>) {
      if (state.method) {
        const indexOfStep = state.method.steps[action.payload].indexOf(
          state.status,
        );
        if (indexOfStep < state.method.steps[action.payload].length - 1) {
          state.status = state.method.steps[action.payload][indexOfStep + 1];
        }
      }
      state.error = null;
    },
    setStep(state, action: PayloadAction<PAYMENT_STEPS>) {
      state.status = action.payload;
      state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    changeField(state, action: PayloadAction<ChangeStringValues>) {
      const { field, value } = action.payload;
      state[field] = value;
      state.error = null;
    },
    changeSelectField(state, action: PayloadAction<ChangeNumberValues>) {
      const { field, value } = action.payload;
      state[field] = value;
      state.error = null;
    },
    setDepositMethods(state, action: PayloadAction<PaymentMethodType[]>) {
      state.depositMethods = action.payload;
    },
    setWithdrawMethods(state, action: PayloadAction<PaymentMethodType[]>) {
      state.withdrawMethods = action.payload;
    },
    setBanks(state, action: PayloadAction<BankDataResponse[]>) {
      const banks = mapBanksB2F(action.payload);
      state.banks = banks;
    },
    setQRCodeData(state, action: PayloadAction<string>) {
      state.qrCodeData = action.payload;
    },
    setLifeTime(state, action: PayloadAction<number | null>) {
      state.lifeTime = action.payload;
    },
    setPaymentPageUrl(state, action: PayloadAction<string>) {
      state.paymentPage = action.payload;
    },
    setPaymentApp(state, action: PayloadAction<PaymentAppsType>) {
      state.paymentApp = action.payload;
    },
    setTransactionId(state, action: PayloadAction<string>) {
      state.transactionId = action.payload;
    },
    setCryptoMethods(state, action: PayloadAction<BanksApiResponse>) {
      state.cryptoMethods = action.payload;
    },
    setMethod(state, action: PayloadAction<SetMethodType>) {
      const { method, type } = action.payload;
      if (
        type === PAYMENT_TYPES.DEPOSIT &&
        method.code !== PAYMENT_METHODS_CODES.CRYPTO_PROCESSING
      ) {
        state.amount = method.defaultValue.toString();
      }
      state.method = method;
      state.type = type;
      state.error = null;
    },
    setCryptoMethod: (
      state,
      action: PayloadAction<{
        cryptoMethod: BanksResponsePaymentMethodCode;
        type: PAYMENT_TYPES;
      }>,
    ) => {
      state.cryptoMethod = action.payload.cryptoMethod;
    },
    setQrCode: (state, action: PayloadAction<string>) => {
      state.qrCode = action.payload;
    },
    setCryptoMinAmount: (state, action: PayloadAction<number>) => {
      state.minAmount = action.payload;
    },
    setCryptoExpDate: (state, action: PayloadAction<string>) => {
      state.expDate = action.payload;
    },
    setWalletAdress: (state, action: PayloadAction<string>) => {
      state.walletAdress = action.payload;
    },
    setDestinationTag: (state, action: PayloadAction<string>) => {
      state.destinationTag = action.payload;
    },
    // setErrors(state, action: PayloadAction<string>) {
    // eslint-disable-next-line unused-imports/no-unused-vars
    // const errors = action.payload;
    // errors.forEach(({ name, error }) => {
    //   if (!!name) {
    //     state[name].error = error;
    //   } else {
    //     state.error = error;
    //   }
    // });
    // },
    setServiceError(state, action: PayloadAction<boolean | null>) {
      state.serviceError = action.payload;
    },
    clearState(state) {
      return {
        ...state,
        account: "",
        accountNumber: "",
        amount: "",
        birthdate: "",
        bkashType: "",
        expire: "",
        card: "",
        email: "",
        phone: "",
        transaction: "",
        bank: null,
        bankCode: null,
        branchCode: "",
        idNumber: "",
        idDate: "",
        iban: "",
        ifscCode: "",
        name: "",
        surname: "",
        paymentApp: null,
        paymentPage: "",
        qrCodeData: "",
        lifeTime: null,
        wallet: "",
        method: null,
        cryptoMethod: null,
        status: PAYMENT_STEPS.DEPOSIT_AMOUNT,
        qrCode: "",
        type: null,
        error: null,
        isLoading: false,
        expDate: "",
        minAmount: null,
        serviceError: null,
        walletAdress: "",
        destinationTag: "",
      };
    },
  },
});

export const {
  changeField,
  changeSelectField,
  clearState,
  setAccount,
  setBkashAccount,
  setBanks,
  setQRCodeData,
  setLifeTime,
  setPaymentApp,
  setPaymentPageUrl,
  setTransactionId,
  stepBack,
  setStep,
  setLoading,
  setMethod,
  stepNext,
  setDepositMethods,
  setWithdrawMethods,
  // setErrors,
  setCryptoMethods,
  setCryptoMethod,
  setQrCode,
  setServiceError,
  setCryptoMinAmount,
  setCryptoExpDate,
  setWalletAdress,
  setDestinationTag,
} = paymentSlice.actions;

export default paymentSlice.reducer;
